import React, { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button, ButtonGroup, ButtonTypes, StandardButton } from '@eika/button';
import { ErrorCard, SuccessCard } from '@eika/card';
import { ConfirmationDialog } from '@eika/confirmation-dialog';
import { IconTypes } from '@eika/icon';
import { ErrorLabel, SuccessLabel, WarningLabel } from '@eika/label';
import { Loader } from '@eika/loader';
import { RichIconTypes } from '@eika/rich-icon';

import {
    useClick2PayAddCardMutation,
    useClick2PayRemoveCardMutation,
} from '@/react-query/hooks/mutation/use-click2pay';
import { useClick2PayProfile } from '@/react-query/hooks/query/use-click2pay';

import { Format } from '@domain/app';
import { Click2PayCustomerProfile, EligibleCardInfo } from '@generated-types';
import useText from '@hooks/use-text';

import './CardRow.scss';

interface Props {
    cards: EligibleCardInfo[];
}

interface CardStates {
    pending: Set<EligibleCardInfo>;
    failed: Set<EligibleCardInfo>;
}

const isActivated = (card: EligibleCardInfo, profile: Click2PayCustomerProfile | undefined): boolean =>
    profile?.cards.some((c2pCard) => c2pCard.eikaCardId === card.eikaCardId) ?? false;

const getLabel = (card: EligibleCardInfo, profile: Click2PayCustomerProfile | undefined, cardStates: CardStates) => {
    if (cardStates.pending.has(card)) {
        return (
            <WarningLabel icon={IconTypes.TIMEGLASS_24} className="card-row--panel-label--pending " text="Forespurt" />
        );
    }

    if (cardStates.failed.has(card)) {
        return <ErrorLabel icon={IconTypes.KRYSS_24} className="card-row--panel-label" text="Feilet – prøv igjen" />;
    }

    if (isActivated(card, profile)) {
        return <SuccessLabel icon={IconTypes.USP_CHECKMARK_24} className="card-row--panel-label" text="Aktivert" />;
    }

    return <div className="card-row--panel-label card-row--panel-label--empty" />;
};

const CardRow = ({ cards }: Props) => {
    const text = useText();
    const profile = useClick2PayProfile();
    const addCardMutation = useClick2PayAddCardMutation();
    const removeCardMutation = useClick2PayRemoveCardMutation();
    const navigate = useNavigate();

    const [cardStates, setCardStates] = useState<CardStates>({
        pending: new Set(),
        failed: new Set(),
    });
    const [pendingRemovalCard, setPendingRemovalCard] = useState<EligibleCardInfo | null>(null);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    if (profile.isError) {
        return (
            <>
                <ErrorCard
                    title={text.messageErrorUps}
                    icon={RichIconTypes.NETTVERKSBRUDD_HARE}
                    description={text.click2PayGenericError}
                    exception={profile.error ?? undefined}
                />
                <ButtonGroup
                    primaryAction={
                        <StandardButton onClick={() => navigate('/')}>{text.click2PayGoToHome}</StandardButton>
                    }
                />
            </>
        );
    }

    const updateCardState = (card: EligibleCardInfo, state: 'pending' | 'success' | 'failed') => {
        setCardStates((prev) => {
            const newPending = new Set(prev.pending);
            const newFailed = new Set(prev.failed);

            newPending.delete(card);
            newFailed.delete(card);

            if (state === 'pending') {
                newPending.add(card);
            } else if (state === 'failed') {
                newFailed.add(card);
            }

            return { pending: newPending, failed: newFailed };
        });
    };

    const executeCardAction = async (card: EligibleCardInfo) => {
        const isCurrentlyActivated = isActivated(card, profile.data);
        const activatedCards = cards.filter((c) => isActivated(c, profile.data));
        const isLastCard = isCurrentlyActivated && activatedCards.length === 1;

        updateCardState(card, 'pending');

        try {
            const response = isCurrentlyActivated
                ? await removeCardMutation.mutateAsync(card.eikaCardId)
                : await addCardMutation.mutateAsync(card.eikaCardId);

            if (response.status === 'SUCCESS') {
                updateCardState(card, 'success');

                if (isLastCard) {
                    setShowSuccessDialog(true);
                }
            } else {
                updateCardState(card, 'failed');
            }
        } catch (error) {
            updateCardState(card, 'failed');
        }
    };

    const handleCardAction = (card: EligibleCardInfo) => {
        const isCurrentlyActivated = isActivated(card, profile.data);
        const activatedCards = cards.filter((c) => isActivated(c, profile.data));

        if (isCurrentlyActivated && activatedCards.length === 1) {
            setPendingRemovalCard(card);
        } else {
            executeCardAction(card);
        }
    };

    const confirmRemoval = () => {
        if (pendingRemovalCard) {
            executeCardAction(pendingRemovalCard);
            setPendingRemovalCard(null);
        }
    };

    const cancelRemoval = () => {
        setPendingRemovalCard(null);
    };

    const handleSuccessDialogClose = () => {
        setShowSuccessDialog(false);
        navigate('/');
    };

    const getButtonText = (card: EligibleCardInfo): string => {
        if (cardStates.failed.has(card) || cardStates.pending.has(card)) {
            return text.click2PayRetryCard;
        }
        return isActivated(card, profile.data) ? text.click2PayRemove : text.click2PayActivate;
    };

    return (
        <div className="card-row">
            {pendingRemovalCard && (
                <ConfirmationDialog
                    title={text.click2payDeleteAccountVerification}
                    id="deletion-confirmation"
                    show
                    onExitFocus={cancelRemoval}
                    footer={
                        <ButtonGroup
                            primaryAction={
                                <StandardButton onClick={confirmRemoval}>{text.click2PayDeleteAccount}</StandardButton>
                            }
                            secondaryAction={
                                <StandardButton buttonType={ButtonTypes.STANDARD_NEGATIVE} onClick={cancelRemoval}>
                                    {text.click2PayCancel}
                                </StandardButton>
                            }
                        />
                    }
                />
            )}

            {showSuccessDialog && (
                <ConfirmationDialog
                    title=""
                    id="account-deleted-success"
                    show
                    onExitFocus={handleSuccessDialogClose}
                    footer={
                        <SuccessCard title={text.click2payAccountDeleted} centeredWithMargin>
                            <Button onClick={handleSuccessDialogClose}>{text.click2PayOk || 'Ok'}</Button>
                        </SuccessCard>
                    }
                />
            )}

            {!pendingRemovalCard &&
                !showSuccessDialog &&
                cards.map((card) => {
                    const isPending = cardStates.pending.has(card);

                    return (
                        <div key={card.eikaCardId} className="card-row--item">
                            <img src={card.imagePath} alt={card.accountName} className="card-row--image" />

                            <div className="card-row--card-content">
                                <p className="card-row--card-content--product-name">{card.accountName}</p>
                                <p>
                                    {Format.cardNumber(card.cardNumber.toString(), {
                                        maskedNumberReplacement: '•',
                                    })}
                                </p>
                            </div>

                            <div className="card-row--right-side">
                                {getLabel(card, profile.data, cardStates)}

                                {isPending ? (
                                    <Loader className="card-row--func-button" mini />
                                ) : (
                                    <Button
                                        className="card-row--func-button"
                                        buttonType={ButtonTypes.STANDARD_NEGATIVE}
                                        onClick={() => handleCardAction(card)}
                                        disabled={profile.isPending}
                                    >
                                        {getButtonText(card)}
                                    </Button>
                                )}
                            </div>
                        </div>
                    );
                })}
        </div>
    );
};
export default CardRow;
